import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import { PageHeader } from "@/components/shell/PageHeader";
import {
  Bell,
  AlertTriangle,
  Info,
  CheckCircle,
  Megaphone,
  Clock,
  Check,
  X,
  Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Notifications = () => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/user/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/user/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const clearAllNotifications = async () => {
    if (!window.confirm("Delete all notifications? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/user/notifications/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications([]);
    } catch (error) {
      console.error("Error clearing notifications:", error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "warning":
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case "success":
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case "announcement":
        return <Megaphone className="w-5 h-5 text-purple-400" />;
      case "update":
        return <Info className="w-5 h-5 text-blue-400" />;
      default:
        return <Bell className="w-5 h-5 text-cyan-400" />;
    }
  };

  const getBgColor = (type) => {
    switch (type) {
      case "warning":
        return "bg-yellow-500/10 border-yellow-500/30";
      case "success":
        return "bg-green-500/10 border-green-500/30";
      case "announcement":
        return "bg-purple-500/10 border-purple-500/30";
      case "update":
        return "bg-blue-500/10 border-blue-500/30";
      default:
        return "bg-slate-800/50 border-slate-700/50";
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === "unread") return !n.read;
    if (filter === "read") return n.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        testId="notifications"
        eyebrow="Signal feed"
        icon={Bell}
        title={<span className="flex items-center gap-3">Notifications{unreadCount > 0 && (<span className="rounded-full bg-cyan-500 px-2 py-0.5 text-sm font-normal text-black" data-testid="notifications-unread-pill">{unreadCount} new</span>)}</span>}
        subtitle="System updates, cron reports and announcements for your workspace."
        actions={<>
          {unreadCount > 0 && (
            <button onClick={markAllAsRead} className="btn-secondary flex items-center gap-2" data-testid="mark-all-read-btn">
              <Check className="w-4 h-4" />Mark all as read
            </button>
          )}
          {notifications.length > 0 && (
            <button onClick={clearAllNotifications} className="flex items-center gap-2 border border-red-500/30 bg-red-500/10 px-4 py-2 text-red-400 transition-all hover:bg-red-500/20" data-testid="clear-all-notifications-btn">
              <Trash2 className="w-4 h-4" />Clear All
            </button>
          )}
        </>}
      />

      {/* Filters */}
      <div className="flex gap-2">
        {[
          { value: "all", label: "All" },
          { value: "unread", label: `Unread (${unreadCount})` },
          { value: "read", label: "Read" }
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-4 py-2 rounded-lg transition-all ${
              filter === f.value
                ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/30"
                : "text-slate-400 hover:text-white hover:bg-slate-800/50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Notifications List */}
      <div className="space-y-3" data-testid="notifications-list">
        {filteredNotifications.length === 0 ? (
          <div className="card-dark p-12 text-center">
            <Bell className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">No notifications</p>
            <p className="text-slate-500 text-sm mt-1">
              {filter === "unread" ? "You're all caught up!" : "Check back later for updates"}
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`p-4 rounded-lg border transition-all ${getBgColor(notification.type)} ${
                !notification.read ? "ring-1 ring-cyan-500/30" : ""
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  {getIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className={`font-medium ${notification.read ? "text-slate-300" : "text-white"}`}>
                        {notification.title}
                      </h4>
                      <p className={`text-sm mt-1 ${notification.read ? "text-slate-500" : "text-slate-400"}`}>
                        {notification.message}
                      </p>
                    </div>
                    {!notification.read && (
                      <button
                        onClick={() => markAsRead(notification.id)}
                        className="text-slate-400 hover:text-cyan-400 transition-colors p-1"
                        title="Mark as read"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Clock className="w-3 h-3 text-slate-500" />
                    <span className="text-xs text-slate-500">
                      {new Date(notification.created_at).toLocaleString()}
                    </span>
                    {!notification.read && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400">New</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Notifications;
